/**
 * ACCESSIBILITY MANAGER
 * Handles global accessibility features like Focus Trapping in modals.
 */

const AccessibilityManager = {
    init() {
        console.log('♿ AccessibilityManager Initialized');
        this.setupAnnouncer();
        this.setupFocusTrapObserver();
        this.setupScreenVisibilityObserver();
        this.setupGlobalKeyboardListeners();
        this.loadPreferences();
    },

    loadPreferences() {
        const dyslexia = localStorage.getItem('a11y_dyslexia') === 'true';
        const highContrast = localStorage.getItem('a11y_contrast') === 'true';

        if (dyslexia) this.setDyslexiaMode(true);
        if (highContrast) this.setHighContrastMode(true);
    },

    toggleDyslexiaMode() {
        const isCurrentlyActive = document.body.classList.contains('dyslexia-mode');
        this.setDyslexiaMode(!isCurrentlyActive);
    },

    setDyslexiaMode(isActive) {
        if (isActive) {
            document.body.classList.add('dyslexia-mode');
            localStorage.setItem('a11y_dyslexia', 'true');
            this.announce('Fuente para dislexia activada');
        } else {
            document.body.classList.remove('dyslexia-mode');
            localStorage.setItem('a11y_dyslexia', 'false');
            this.announce('Fuente para dislexia desactivada');
        }
        this.updateButtonsUI();
    },

    toggleHighContrastMode() {
        const isCurrentlyActive = document.body.classList.contains('high-contrast-mode');
        this.setHighContrastMode(!isCurrentlyActive);
    },

    setHighContrastMode(isActive) {
        if (isActive) {
            document.body.classList.add('high-contrast-mode');
            localStorage.setItem('a11y_contrast', 'true');
            this.announce('Modo de alto contraste activado');
        } else {
            document.body.classList.remove('high-contrast-mode');
            localStorage.setItem('a11y_contrast', 'false');
            this.announce('Modo de alto contraste desactivado');
        }
        this.updateButtonsUI();
    },

    updateButtonsUI() {
        const btnDyslexia = document.getElementById('btnToggleDyslexia');
        const btnContrast = document.getElementById('btnToggleContrast');

        if (btnDyslexia) {
            const isActive = document.body.classList.contains('dyslexia-mode');
            btnDyslexia.textContent = isActive ? '🔤 Desactivar Fuente Dislexia' : '🔤 Activar Fuente Dislexia';
            if (isActive) btnDyslexia.classList.add('btn-primary');
            else btnDyslexia.classList.remove('btn-primary');
        }

        if (btnContrast) {
            const isActive = document.body.classList.contains('high-contrast-mode');
            btnContrast.textContent = isActive ? '👁️ Desactivar Alto Contraste' : '👁️ Activar Alto Contraste';
            if (isActive) btnContrast.classList.add('btn-primary');
            else btnContrast.classList.remove('btn-primary');
        }
    },

    setupAnnouncer() {
        if (!document.getElementById('a11y-announcer')) {
            const announcer = document.createElement('div');
            announcer.id = 'a11y-announcer';
            announcer.setAttribute('aria-live', 'polite');
            announcer.setAttribute('aria-atomic', 'true');
            announcer.style.position = 'absolute';
            announcer.style.width = '1px';
            announcer.style.height = '1px';
            announcer.style.padding = '0';
            announcer.style.margin = '-1px';
            announcer.style.overflow = 'hidden';
            announcer.style.clip = 'rect(0,0,0,0)';
            announcer.style.border = '0';
            document.body.appendChild(announcer);
        }
    },

    announce(message, priority = 'polite') {
        const announcer = document.getElementById('a11y-announcer');
        if (announcer) {
            announcer.setAttribute('aria-live', priority);
            announcer.textContent = '';
            setTimeout(() => {
                announcer.textContent = message;
            }, 50);
        }
    },

    /**
     * Observes the DOM for active modals to trap focus
     */
    setupFocusTrapObserver() {
        // Observer to detect when a modal becomes visible
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
                    const target = mutation.target;

                    // Check if it's a modal and if it's now active/visible
                    if (target.classList.contains('modal') || target.id.includes('Modal')) {
                        const isHidden = target.classList.contains('hidden') || target.style.display === 'none';
                        const isActive = target.classList.contains('active') || !isHidden;

                        if (isActive) {
                            this.trapFocus(target);
                        }
                    }
                }
            });
        });

        // Start observing body for subtree changes (to catch dynamically created modals)
        observer.observe(document.body, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['class', 'style']
        });
    },

    /**
     * Observes .screen elements to dynamically toggle aria-hidden
     */
    setupScreenVisibilityObserver() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const target = mutation.target;
                    if (target.classList.contains('screen')) {
                        const isHidden = target.classList.contains('hidden');
                        target.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
                    }
                }
            });
        });

        const screens = document.querySelectorAll('.screen');
        screens.forEach(screen => {
            // Set initial state
            const isHidden = screen.classList.contains('hidden');
            screen.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
            
            // Observe changes
            observer.observe(screen, { attributes: true, attributeFilter: ['class'] });
        });
    },

    /**
     * Traps focus within the specified element
     */
    trapFocus(element) {
        // Find all focusable elements
        const focusableElementsString = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        let focusableContent = element.querySelectorAll(focusableElementsString);

        // Filter out hidden elements
        focusableContent = Array.from(focusableContent).filter(el => el.offsetParent !== null);

        if (focusableContent.length === 0) return;

        const firstFocusableElement = focusableContent[0];
        const lastFocusableElement = focusableContent[focusableContent.length - 1];

        // Focus the first element immediately
        setTimeout(() => firstFocusableElement.focus(), 100);

        // Add keydown listener to trap tab
        element.addEventListener('keydown', function (e) {
            const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

            if (!isTabPressed) return;

            if (e.shiftKey) { // if shift key pressed for shift + tab combination
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus(); // last element focus
                    e.preventDefault();
                }
            } else { // if tab key is pressed
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus(); // first element focus
                    e.preventDefault();
                }
            }
        });
    },

    setupGlobalKeyboardListeners() {
        // Optional: Close modals on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                const activeModal = document.querySelector('.modal.active, .modal:not(.hidden)');
                if (activeModal) {
                    // Try to find a close button first
                    const closeBtn = activeModal.querySelector('.close-btn, .btn-secondary, button[onclick*="close"]');
                    if (closeBtn) closeBtn.click();
                    else activeModal.classList.add('hidden'); // Fallback forced close
                }
            }
        });
    }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    AccessibilityManager.init();
});

window.AccessibilityManager = AccessibilityManager;
